import React, { useState, useEffect } from 'react';

/**
 * Calculates the time remaining until the target date.
 * @param {Date} targetDate - The future date and time to count down to.
 * @returns {object} An object containing days, hours, minutes, seconds remaining, or null if the target date is in the past.
 */
const calculateTimeLeft = (targetDate) => {
  const difference = +targetDate - +new Date(); // Difference in milliseconds
  let timeLeft = null;

  if (difference > 0) {
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }

  return timeLeft;
};

/**
 * A component that displays a countdown timer to a target date.
 * @param {object} props - Component props.
 * @param {Date} props.targetDate - The target date for the countdown.
 * @param {function} props.onFourSecondsLeft - Callback when 4 seconds are left (e.g. play 3-2-1 audio).
 * @param {function} props.onCountdownComplete - Callback when countdown reaches 0.
 */
const CountdownTimer = ({ targetDate, onFourSecondsLeft, onCountdownComplete }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetDate));
  const [hasFiredFourSeconds, setHasFiredFourSeconds] = useState(false);
  const [hasFiredComplete, setHasFiredComplete] = useState(false);

  useEffect(() => {
    // Update the countdown every second
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(targetDate);
      setTimeLeft(newTimeLeft);

      // Countdown reached 0 - fire complete callback only when we TRANSITIONED from counting down.
      // Don't fire when mounting with targetDate already in the past (avoids loader loop).
      if (!newTimeLeft && !hasFiredComplete && hasFiredFourSeconds) {
        setHasFiredComplete(true);
        onCountdownComplete && onCountdownComplete();
      }
      // Check if exactly 4 seconds are left and we haven't fired the event yet
      else if (newTimeLeft &&
          newTimeLeft.days === 0 &&
          newTimeLeft.hours === 0 &&
          newTimeLeft.minutes === 0 &&
          newTimeLeft.seconds === 4 &&
          !hasFiredFourSeconds) {
        setHasFiredFourSeconds(true);
        onFourSecondsLeft && onFourSecondsLeft();
      }
    }, 1000);

    // Clear interval on component unmount
    return () => clearInterval(timer);
  }, [targetDate, hasFiredFourSeconds, hasFiredComplete, onFourSecondsLeft, onCountdownComplete]);

  // Format the time left into a readable string or components
  const formatTime = (time) => {
    if (!time) {
      return <span>Auction should be starting soon or has passed!</span>;
    }

    const parts = [];
    if (time.days > 0) parts.push(`${time.days}d`);
    if (time.hours > 0 || time.days > 0) parts.push(`${time.hours}h`); // Show hours if days exist
    if (time.minutes > 0 || time.hours > 0 || time.days > 0) parts.push(`${time.minutes}m`); // Show minutes if hours/days exist
    parts.push(`${time.seconds}s`); // Always show seconds

    return parts.join(' ');
  };

  return (
    <div className="text-2xl font-semibold text-red-600 bg-red-100 px-4 py-2 rounded-lg border border-red-300 shadow-inner">
      {formatTime(timeLeft)}
    </div>
  );
};

export default CountdownTimer; 